import React, { useEffect, useState, useCallback } from "react";
import {
  Table,
  Card,
  Button,
  Input,
  Tabs,
  Spin,
  Pagination,
  Tooltip,
} from "antd";
import { useNavigate } from "react-router-dom";
import { SearchOutlined, CloseOutlined } from "@ant-design/icons";
import { getRequest } from "../../api/http";
import { toast } from "react-toastify";
import "./organisation-list.scss";
import userServiceApi from "../../api/userServiceApi";
import Line from "../line/index";
import listView from "../../assets/images/list-view.svg";
import cardView from "../../assets/images/card-view.svg";
import listViewSelected from "../../assets/images/list-view-selected.svg";
import cardViewSelected from "../../assets/images/card-view-selected.svg";
import Leftarrow from "../../assets/images/LeftArrow.svg";
import nologo from "../../assets/images/noimage.png";
import { inputFieldCharLimit } from "../../config/index";
import { truncate } from "../../utils/helper";
import { debounce } from "lodash";

const { TabPane } = Tabs;

export default function OrganisationList() {
  const navigate = useNavigate();
  const [boolaforg, setBoolAforg] = useState(false);
  const [boolglorg, setBoolGlorg] = useState(false);
  const [boolmsorg, setBoolMsorg] = useState(false);
  const [booltzorg, setBoolTzorg] = useState(false);
  const [aforg, setAforg] = useState("");
  const [glorg, setGlorg] = useState("");
  const [msorg, setMsorg] = useState("");
  const [tzorg, setTzorg] = useState("");
  const [loadingOrganizations, setLoadingOrganizations] = useState(false);
  const [viewSelected, setViewSelected] = useState("card");
  const [activeTab, setActiveTab] = useState("fullMembers");
  const [fullMembers, setFullMembers] = useState([]);
  const [hubMembers, setHubMembers] = useState([]);
  const [pageSize, setPageSize] = useState(10);
  const [totalOrgs, setTotalOrgs] = useState();
  const [pageNumber, setPageNumber] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const selectedPartner = sessionStorage.getItem("partnerId");

  // ✅ Only allow safe alphanumeric input (no special chars)
  const nuclearSanitize = (input) => {
    if (input === undefined || input === null) return "";
    if (typeof input !== "string") return "";
    const sanitized = input.replace(/[^a-zA-Z0-9 ]/g, "");
    console.log("🧹 Sanitized:", `"${input}"`, "→", `"${sanitized}"`);
    return sanitized;
  };

  const handlePagNumber = (number) => {
    setPageNumber(number - 1);
  };

  const columns = [
    {
      dataIndex: "name",
      className: "name-org",
      key: "name",
      render: (t) => (
        <div
          style={{
            fontSize: "15px",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            maxWidth: "300px",
          }}
        >
          {truncate(t, 25)}
        </div>
      ),
    },
    {
      dataIndex: "mediaLink",
      key: "mediaLink",
      width: 100,
      className: "logo",
      render: (t, r) =>
        r.mediaLink ? (
          <img
            style={{ height: "65px" }}
            src={`${r.mediaLink}`}
            className="image-logo"
            alt={r.name}
          />
        ) : (
          <img
            src={nologo}
            alt="No logo"
            style={{ objectFit: "contain", display: "flex", margin: "auto" }}
          />
        ),
    },
  ];

  // ✅ Fixed working fetch function
  const fetchOrganizations = async (searchValue = null) => {
    try {
      setLoadingOrganizations(true);

      const isActiveMember = activeTab === "hubMembers";
      const sanitizedSearch = nuclearSanitize(searchValue ?? searchTerm);

      console.log("🚀 Fetching organizations | search:", sanitizedSearch);

      const response = await getRequest(
        userServiceApi.getOrgUnderPartner(
          pageSize,
          pageNumber,
          selectedPartner,
          isActiveMember,
          boolaforg,
          boolglorg,
          boolmsorg,
          booltzorg,
          aforg,
          glorg,
          msorg,
          tzorg,
          sanitizedSearch
        )
      );

      setTotalOrgs(response?.data?.totalCount);
      return response.data?.result || [];
    } catch (error) {
      console.error("❌ Error fetching organizations data:", error);
      toast.error("Failed to fetch organization data");
      return [];
    } finally {
      setLoadingOrganizations(false);
    }
  };

  // 🔍 Search handling
  const handleSearchChange = (e) => {
    const rawValue = e.target.value || "";
    const sanitizedValue = nuclearSanitize(rawValue);

    if (rawValue !== sanitizedValue) {
      console.warn("🚫 Blocked dangerous characters in input");
    }

    setSearchTerm(sanitizedValue);

    if (sanitizedValue.trim().length > 0) {
      debouncedSearch(sanitizedValue);
    } else {
      handleViewOrganizations();
    }
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    handleViewOrganizations();
  };

  const handlePressEnter = (e) => {
    const rawValue = e.target.value || "";
    const sanitizedValue = nuclearSanitize(rawValue);
    if (sanitizedValue.trim().length > 0) {
      handleViewOrganizations(sanitizedValue);
    }
  };

  const debouncedSearch = useCallback(
    debounce((value) => {
      handleViewOrganizations(value);
    }, 600),
    [
      selectedPartner,
      activeTab,
      boolaforg,
      boolglorg,
      boolmsorg,
      booltzorg,
      aforg,
      glorg,
      msorg,
      tzorg,
      pageSize,
      pageNumber,
    ]
  );

  const handleViewOrganizations = async (searchValue = null) => {
    const isActiveMember = activeTab === "hubMembers";

    try {
      const organizationsData = await fetchOrganizations(searchValue);

      if (isActiveMember) {
        setHubMembers(organizationsData);
      } else {
        setFullMembers(organizationsData);
      }
    } catch (error) {
      console.error("Error handling organizations:", error);
      if (isActiveMember) {
        setHubMembers([]);
      } else {
        setFullMembers([]);
      }
    }
  };

  useEffect(() => {
    handleViewOrganizations();
  }, [activeTab, pageNumber, aforg, glorg, msorg, tzorg]);

  useEffect(() => {
    setBoolAforg(false);
    setBoolGlorg(false);
    setBoolMsorg(false);
    setBoolTzorg(false);
    setAforg("");
    setGlorg("");
    setMsorg("");
    setTzorg("");
  }, [activeTab]);

  // 🔠 Alphabet filters
  const handleAForg = () => {
    setAforg(!boolaforg ? "A-F" : "");
    setBoolAforg(!boolaforg);
  };

  const handleGLorg = () => {
    setGlorg(!boolglorg ? "G-L" : "");
    setBoolGlorg(!boolglorg);
  };

  const handleMSorg = () => {
    setMsorg(!boolmsorg ? "M-S" : "");
    setBoolMsorg(!boolmsorg);
  };

  const handleTZorg = () => {
    setTzorg(!booltzorg ? "T-Z" : "");
    setBoolTzorg(!booltzorg);
  };

  const getCurrentData = () =>
    activeTab === "hubMembers" ? hubMembers : fullMembers;

  const getEmptyMessage = () => {
    const currentData = getCurrentData();
    const tabName = activeTab === "hubMembers" ? "Hub Members" : "Full Members";

    if (searchTerm && currentData.length === 0) {
      return `No ${tabName.toLowerCase()} found for "${searchTerm}"`;
    } else if (currentData.length === 0) {
      return `No ${tabName.toLowerCase()} available`;
    }
    return null;
  };

  const emptyMessage = getEmptyMessage();

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  return (
    <div className="main-container-oranagisation-list">
      <div className="tabContainer">
        <div className="filters-container">
          <div className="partner-heading">
            <img
              className="back-btn-arrow"
              src={Leftarrow}
              alt="Back"
              onClick={() => {
                navigate("/admin");
                sessionStorage.setItem("activeKey", 1);
              }}
              style={{ cursor: "pointer" }}
            />
            <p>Organisations List</p>
          </div>
          <div className="buttons">
            <div className="searchbar-containers">
              <Tooltip
                placement="top"
                title="Allowed: letters, numbers, spaces, hyphens (-), and underscores (_)."
              >
                <Input
                  className="searchbar"
                  placeholder="Search Organisations (letters and numbers only)"
                  suffix={
                    searchTerm ? (
                      <CloseOutlined
                        style={{
                          color: "#CC0E2D",
                          verticalAlign: "middle",
                          cursor: "pointer",
                        }}
                        onClick={handleClearSearch}
                      />
                    ) : (
                      <SearchOutlined
                        style={{ color: "#CC0E2D", verticalAlign: "middle" }}
                      />
                    )
                  }
                  onChange={handleSearchChange}
                  onPressEnter={handlePressEnter}
                  value={searchTerm}
                  maxLength={inputFieldCharLimit}
                />
              </Tooltip>
            </div>
            <Line />
            <div className="alphabets-filter">
              <Button
                id="A-F-btn"
                className={`${!boolaforg
                  ? "alpha-filter-btn first-btn"
                  : "alpha-filter-btn-select first-btn"
                  }`}
                onClick={handleAForg}
              >
                <span className="alphas">A-F</span>
              </Button>
              <Button
                id="G-L-btn"
                className={`${!boolglorg ? "alpha-filter-btn" : "alpha-filter-btn-select"
                  }`}
                onClick={handleGLorg}
              >
                <span className="alphas">G-L</span>
              </Button>
              <Button
                id="M-S-btn"
                className={`${!boolmsorg ? "alpha-filter-btn" : "alpha-filter-btn-select"
                  }`}
                onClick={handleMSorg}
              >
                <span className="alphas">M-S</span>
              </Button>
              <Button
                id="T-Z-btn"
                className={`${!booltzorg ? "alpha-filter-btn" : "alpha-filter-btn-select"
                  }`}
                onClick={handleTZorg}
              >
                <span className="alphas">T-Z</span>
              </Button>
            </div>
            <Line />
            {viewSelected === "list" ? (
              <img
                src={listViewSelected}
                alt="listviewSelected"
                style={{ margin: "0px 10px" }}
              />
            ) : (
              <img
                src={listView}
                alt="listview"
                style={{ cursor: "pointer", margin: "0px 10px" }}
                onClick={() => setViewSelected("list")}
              />
            )}
            <Line />
            {viewSelected === "card" ? (
              <img
                src={cardViewSelected}
                alt="cardViewSelected"
                style={{ marginLeft: "10px" }}
              />
            ) : (
              <img
                src={cardView}
                alt="cardView"
                style={{ cursor: "pointer", marginLeft: "10px" }}
                onClick={() => setViewSelected("card")}
              />
            )}
          </div>
        </div>

        <Tabs
          activeKey={activeTab}
          style={{ marginTop: "10px" }}
          onChange={(key) => {
            setPageNumber(0);
            setActiveTab(key);
          }}
        >
          {["fullMembers", "hubMembers"].map((key) => {
            const data = key === "hubMembers" ? hubMembers : fullMembers;
            return (
              <TabPane
                tab={key === "hubMembers" ? "Hub Members" : "Full Members"}
                key={key}
              >
                {loadingOrganizations ? (
                  <Spin
                    size="large"
                    style={{ margin: "50px auto", display: "block" }}
                    tip="Loading organizations..."
                  />
                ) : (
                  <>
                    {viewSelected === "list" ? (
                      <div
                        style={
                          data && data.length === 0
                            ? { display: "block", justifyContent: "space-between", }
                            : null
                        }
                      >
                        {data.length > 0 ? (
                          <>
                            <div style={{ flex: 1, marginRight: "10px" }}>
                              <Table
                                rowClassName={() => "rowClassName1"}
                                columns={columns}
                                dataSource={data.slice(0, 5)}
                                pagination={false}
                                rowKey="id"
                                locale={{
                                  emptyText: (
                                    <div
                                      style={{
                                        textAlign: "center",
                                        padding: "40px",
                                      }}
                                    >
                                      {emptyMessage} hello 11
                                    </div>
                                  ),
                                }}
                              />
                            </div>
                            <div style={{ flex: 1, marginLeft: "10px" }}>
                              {data.slice(5, 10).length > 0 && (
                                <Table
                                  rowClassName={() => "rowClassName1"}
                                  columns={columns}
                                  dataSource={data.slice(5, 10)}
                                  pagination={false}
                                  rowKey="id"
                                />
                              )}
                            </div>
                          </>
                        ) : (
                          <div
                            className="partner-list-container"
                            style={
                              data && data.length === 0
                                ? { display: "block" }
                                : null
                            }
                          >
                            <div className="empty-state">
                              <h3
                                className="placeholder"
                                style={{
                                  textAlign: "center",
                                  margin: "50px 0",
                                }}
                              >
                                {emptyMessage}
                              </h3>
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div
                        className="partner-list-container"
                        style={
                          data && data.length === 0
                            ? { display: "block" }
                            : null
                        }
                      >
                        {data.length > 0 ? (
                          data.map((organisation) => (
                            <Card
                              className="partner-card"
                              key={organisation.id}
                              size="small"
                            >
                              <div className="card-header-container"></div>
                              <div className="card-main-container">
                                <div className="partner-logo-container">
                                  {organisation.mediaLink ? (
                                    <img
                                      src={`${organisation.mediaLink}`}
                                      alt={`${organisation.name}`}
                                      className="image-logo"
                                    />
                                  ) : (
                                    <img
                                      src={nologo}
                                      alt="No logo"
                                      style={{
                                        objectFit: "contain",
                                        display: "flex",
                                        margin: "auto",
                                      }}
                                    />
                                  )}
                                </div>
                                <div className="partner-configurations-container">
                                  <div className="card-heading">
                                    {truncate(organisation.name, 25)}
                                  </div>
                                </div>
                              </div>
                            </Card>
                          ))
                        ) : (
                          <div className="empty-state">
                            <h3
                              className="placeholder"
                              style={{
                                textAlign: "center",
                                margin: "50px 0",
                              }}
                            >
                              {emptyMessage}
                            </h3>
                          </div>
                        )}
                      </div>
                    )}
                    {data.length > 0 && (
                      <div className="pagination-container">
                        <Pagination
                          className="pagination"
                          current={pageNumber + 1}
                          pageSize={pageSize}
                          total={totalOrgs}
                          showTotal={(total) => `Total ${total} Organisations`}
                          onChange={(page) => handlePagNumber(page)}
                        />
                      </div>
                    )}
                  </>
                )}
              </TabPane>
            );
          })}
        </Tabs>
      </div>
    </div>
  );
}
